import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useModal } from '../../../context/ModalContext';
import Payment from '../../../service/pay/pay';
import Receipt from '../../components/pay/receipt';
import './receipt-print.css';

interface PaymentHistoryItem {
  idx: number;
  pay_id: number;
  created_at: string;
  name: string;
  amount: number;
  method: string;
  installment: number;
  status: string;
  start_date: string;
  end_date: string;
}

interface DetailHistory {
  std_prc: number;
  is_discount: string;
  description: string;
  discount_amount: number;
  total_prc: number;
  good_name: string;
  order_id: string;
  plan_id: string;
  method: string;
  card_bank: string;
  installment: number;
  created_at: string;
  appl_num: number;
  account_num: string;
  bank_name: string;
  account_holder: string;
  input_date: string;
  input_time: string;
}

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>(
    [],
  );
  const [selectedPayId, setSelectedPayId] = useState<number | null>(null);
  const [detailHistory, setDetailHistory] = useState<DetailHistory>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PaymentHistoryItem | null>(
    null,
  );
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const { modalDispatch } = useModal();

  const getpaymentHistory = async () => {
    try {
      const response = await Payment.paymentHistory();
      setPaymentHistory(response.data);
    } catch (error) {
      alert('상품 목록을 불러오는 데 실패했습니다.');
    }
  };

  const getDetailHistory = async (payId: number) => {
    try {
      const response = await Payment.detailHistory(payId);
      setDetailHistory(response.data);
    } catch (error) {
      console.error('getDetailHistory 함수에서 에러 발생');
    }
  };

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  useEffect(() => {
    const fetchInit = async () => {
      await Promise.all([getpaymentHistory()]);
    };
    fetchInit();
  }, []);

  useEffect(() => {
    if (isModalOpen && selectedPayId !== null) {
      getDetailHistory(selectedPayId);
    }
  }, [isModalOpen, selectedPayId]);

  // 잔여일 계산 함수
  const calculateRemainingDays = (endDateStr?: string) => {
    if (!endDateStr) return null;

    const endDate = new Date(endDateStr);
    const today = new Date();

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // 밀리초 → 일

    return diffDays;
  };

  const formatDate = (isoString: string | null | undefined) => {
    if (!isoString) return '-';

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '-'; // 날짜 파싱 실패 시

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}년 ${month}월 ${day}일`;
  };

  function formatDateTime(created_at: string): string {
    const date = new Date(created_at.replace(' ', 'T')); // ISO 형식으로 변환

    const pad = (n: number) => n.toString().padStart(2, '0');

    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${month}.${day} ${hours}:${minutes}`;
  }

  const navigate = useNavigate();

  // const handlePrint = () => {
  //   if (!printRef.current) return;

  //   const printContents = printRef.current.innerHTML;

  //   const printWindow = window.open('', '', 'width=600,height=800');
  //   if (printWindow) {
  //     printWindow.document.open();
  //     printWindow.document.write(`
  //     <html>
  //       <head>
  //         <title>영수증</title>
  //         <link rel="stylesheet" type="text/css" href="/receipt-print.css" />
  //       </head>
  //       <body>
  //         ${printContents}
  //       </body>
  //     </html>
  //   `);
  //     printWindow.document.close();
  //     printWindow.focus();
  //     setTimeout(() => {
  //       printWindow.print();
  //       printWindow.close();
  //     }, 500);
  //   }
  // };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <PaymentDiv>
        {paymentHistory.length === 0 ? (
          <>
            <None>
              <div>구매한 상품이 없습니다.</div>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#616161',
                }}
              >
                이용권을 업그레이드하고
                <br />
                경매 투자 성공률을 높혀보세요!
              </div>
              <RefundButton
                onClick={() => {
                  navigate('/payment/plan');
                }}
                style={{ marginTop: '20px' }}
              >
                요금제 보러가기
              </RefundButton>
            </None>
          </>
        ) : (
          <>
            <Title>결제 내역</Title>
            {paymentHistory?.map((item) => (
              <Row key={item.idx}>
                <Created>{formatDateTime(item.created_at)} 결제</Created>
                <NameDiv>
                  <Name>{item.name} </Name>
                  <Img
                    src="/assets/images/arrow/arrow_right_24.svg"
                    alt="arrow"
                    onClick={() => {
                      setSelectedPayId(item.pay_id);
                      setSelectedItem(item);
                      setIsModalOpen(true);
                    }}
                  />
                </NameDiv>

                <Price>{item.amount.toLocaleString()}원</Price>
                <MembershipDate>
                  <div>시작일 {formatDate(item.start_date)}</div>
                  <div>종료일 {formatDate(item.end_date)}</div>
                </MembershipDate>
                <RowBottom>
                  <Installment>
                    잔여일
                    {(() => {
                      const remainingDays = calculateRemainingDays(
                        item?.end_date,
                      );
                      return remainingDays !== null && remainingDays > 0 ? (
                        <BlueSpan> {remainingDays}일</BlueSpan>
                      ) : (
                        <span> -</span>
                      );
                    })()}
                  </Installment>

                  <div
                    style={{
                      width: '1px',
                      height: '11px',
                      backgroundColor: '#E1E2E4',
                    }}
                  />
                  <Method>{item.method}</Method>

                  <div
                    style={{
                      width: '1px',
                      height: '11px',
                      backgroundColor: '#E1E2E4',
                    }}
                  />
                  <Status status={item.status}>{item.status}</Status>
                </RowBottom>
              </Row>
            ))}
          </>
        )}
      </PaymentDiv>
      {isModalOpen && (
        <Container onClick={() => setIsModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <Header>
              <div>결제 상세</div>
              <img
                src="/assets/images/close/close-dark-gray.svg"
                alt="Close"
                style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                onClick={() => setIsModalOpen(false)}
              />
            </Header>
            <div ref={printRef} className="receipt-print">
              <div className="total">
                <div className="left-text" style={{ fontSize: '14px' }}>
                  총 결제 금액
                </div>
                <div className="total-price">
                  {detailHistory?.total_prc.toLocaleString('ko-KR')}원
                </div>
              </div>

              <div className="modal-content">
                <div className="list" style={{ marginTop: '24px' }}>
                  <div className="left-text">기본 상품 금액</div>
                  <div className="right-text" style={{ color: '#616161' }}>
                    {detailHistory?.std_prc.toLocaleString('ko-KR')}원
                  </div>
                </div>
                <div className="list">
                  <div className="left-text">할인 금액</div>
                  <div className="right-text">
                    {detailHistory?.is_discount === 'N'
                      ? '0원'
                      : `${detailHistory?.description} 할인 (-${detailHistory?.discount_amount.toLocaleString('ko-KR')}원)`}
                  </div>
                </div>
                <div className="list">
                  <div className="left-text">총 결제 금액</div>
                  <div className="right-text">
                    {detailHistory?.total_prc.toLocaleString('ko-KR')}원
                  </div>
                </div>
                <div className="divider" />
              </div>

              <div className="modal-content">
                <div className="list">
                  <div className="left-text">상품명</div>
                  <div className="right-text">{detailHistory?.good_name}</div>
                </div>
                <div className="list">
                  <div className="left-text">결제 수단</div>
                  <div className="right-text">{detailHistory?.method}</div>
                </div>
                <div className="list">
                  <div className="left-text">결제카드</div>
                  <div className="right-text">
                    {detailHistory?.method === '신용카드'
                      ? detailHistory?.card_bank
                      : '-'}
                  </div>
                </div>
                <div className="list">
                  <div className="left-text">할부기간</div>
                  <div className="right-text">
                    {detailHistory?.method === '신용카드'
                      ? detailHistory?.installment === 0 ||
                        detailHistory?.installment === null
                        ? '일시불'
                        : `${detailHistory?.installment}개월`
                      : '-'}
                  </div>
                </div>
                <div className="list">
                  <div className="left-text">결제일시</div>
                  <div className="right-text">
                    {detailHistory?.created_at.replace(/-/g, '.')}
                  </div>
                </div>
                {detailHistory?.method === '신용카드' ? (
                  <div className="list">
                    <div className="left-text">승인번호</div>
                    <div className="right-text">
                      {detailHistory?.appl_num === null
                        ? '-'
                        : detailHistory?.appl_num}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="list">
                      <div className="left-text">결제상태</div>
                      <div className="right-text">{selectedItem?.status}</div>
                    </div>
                    <div className="list">
                      <div className="left-text">가상계좌</div>
                      <div className="right-text blue-text">
                        {detailHistory?.bank_name} {detailHistory?.account_num}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {selectedItem?.status !== '입금 대기중' && (
              <Bottom>
                <Button
                  onClick={() => {
                    modalDispatch({ type: 'I' });
                  }}
                >
                  환불문의
                </Button>
                <Button
                  onClick={() => {
                    console.log('클릭');
                    setIsReceiptModalOpen(!isReceiptModalOpen);
                  }}
                >
                  영수증
                </Button>
              </Bottom>
            )}
          </Modal>
          {isReceiptModalOpen && detailHistory && (
            <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 2000 }}>
              <Receipt
                onClose={() => setIsReceiptModalOpen(false)}
                detailHistory={detailHistory}
              />
            </div>
          )}
        </Container>
      )}
    </div>
  );
};

export default PaymentHistory;

const PaymentDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Title = styled.div`
  align-self: stretch;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const RefundButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 48px;
  background-color: transparent;
  border-radius: 8px;
  border: 1px solid #008aff;
  font-size: 14px;
  font-weight: 500;
  color: #008aff;
  cursor: pointer;
`;

const Img = styled.img`
  cursor: pointer;
  width: 20px;
  height: 20px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  gap: 8px;
  padding: 20px;
`;

const None = styled.div`
  width: 100%;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const Cell = styled.div`
  width: 100%;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  font-size: 16px;
  color: #616161;
`;

const Created = styled(Cell)`
  font-size: 14px;
`;

const NameDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const Name = styled(Cell)`
  display: block;
  overflow: hidden; // 을 사용해 영역을 감출 것
  text-overflow: ellipsis; // 로 ... 을 만들기
  white-space: nowrap; // 아래줄로 내려가는 것을 막기위해
  word-break: break-all;
  font-weight: 700;
  font-size: 16px;
  color: #222222;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const Price = styled(Cell)`
  font-weight: 700;
  font-size: 16px;
  color: #222222;
`;

const MembershipDate = styled(Cell)`
  font-size: 13px;
  color: #616161;
  font-weight: 500;
  display: flex;
  flex-direction: column;
`;

const Method = styled.div``;
const Installment = styled.div``;
const Status = styled.div<{ status: string }>`
  color: ${({ status }) => (status === '입금 대기중' ? '#008AFF' : '#616161')};
`;

const RowBottom = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  color: #616161;
  font-size: 13px;
  font-weight: 500;
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); // 회색 배경
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const Modal = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  padding: 0px 16px;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
`;

const Header = styled.div`
  width: 100%;
  height: 66px;
  display: flex;
  flex-direction: row;
  font-size: 18px;
  font-weight: 700;
  align-items: center;
  justify-content: space-between;
`;

const Total = styled.div`
  width: 100%;
  height: 105px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const TotalPrice = styled.div`
  font-weight: 700;
  font-size: 20px;
`;

const ModalContent = styled.div`
  width: 100%;
  height: 190px;
  display: flex;
  flex-direction: column;
  //padding: 24px 16px;
  gap: 24px;
`;

const List = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 16px;
`;

const LeftText = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #616161;
`;

const RightText = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #222222;
`;

const Bottom = styled.div`
  width: 100%;
  height: 89px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: end;
`;

const Button = styled.div`
  padding: 9px 24px;
  border-radius: 8px;
  background-color: rgba(112, 115, 124, 0.08);
  cursor: pointer;
`;

const BlueSpan = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #008aff;
`;
